import { useTranslation } from '@thoughtspot/i18n';
import { collectEvent, UserAction, userActionTypeEnum, } from '@thoughtspot/metrics';
import { PqlCaptureEventId, sendOnboardingEvent, } from '@thoughtspot/onboarding-event-service';
import { getAdminGroupGuid, getAllGroupGUID, getGuidsToSkip, getSubscriptionType, getUserGuid, isOrgUiEnabled, isWelcomeEmailEnabled, optimizeAddUserFlowEnabled, SubscriptionType, } from '@thoughtspot/session-service';
import { isOktaEnabled } from '@thoughtspot/system-config-service';
import _ from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useGetOrgsListLazyQuery, useGetUserOrgsByUserIdLazyQuery, } from "../../../../services/org/org-service";
import { useCreateUserMutation, useGetUserAndGroupsQuery, useGetUserDetailsByIdWithUsersListLazyQuery, useSendWelcomeEmailMutation, useUpdateUserMutation, } from "../../../../services/user-group/user-group-service";
import { getTranslatedInfoForMessageCode } from "../../../../utils/error-handling/error-strings-mapping";
import { getErrorObjectFromGraphQLErrors } from "../../../../utils/error-handling/error-utils";
import { captureFailureEvent, CHECKBOX_SECTION_PAGINATION_BATCHSIZE, getErrorCode, MODE, SHARING_VISIBILITY, } from '../../util';
import { hasImplicitAdminAccess, orgListParams, } from '../util';
import { CreateEditForm } from './create-edit-form.container';
export var FIELD;
(function (FIELD) {
    FIELD["NAME"] = "name";
    FIELD["DESCRIPTION"] = "description";
})(FIELD || (FIELD = {}));
const ADMINISTRATOR_GROUP_NAME = 'Administrator';
export const CreateEditModal = ({ isModalOpened, closeModal, isEditMode = false, setIsRefresh = _.noop, setIsLoading, successBannerMode, userId = '', currentOrgId = 0, }) => {
    const transientUser = useRef(null);
    const isFilterTouched = useRef(false);
    const selectedGroupsRef = useRef(null);
    const selectedOrgsRef = useRef(null);
    const { t } = useTranslation();
    const [isEditReady, setIsEditReady] = useState(false);
    const [groupListIds, setGroupListIds] = useState([]);
    const [orgListIds, setOrgListIds] = useState([]);
    const [preSelectedGroupListIds, setPreSelectedGroupListIds] = useState([]);
    const [preSelectedOrgListIds, setPreSelectedOrgListIds] = useState([]);
    const [userGroupList, setUserGroupList] = useState([]);
    const [orgList, setOrgList] = useState([]);
    const [filteredUserGroupList, setFilteredUserGroupList] = useState([]);
    const [filteredOrgList, setFilteredOrgList] = useState([]);
    const [errorMessage, setErrorMessage] = useState(null);
    const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);
    const id = useRef('');
    const [groupListInput, setGroupListInput] = useState({
        searchText: '',
        guidsToSkip: getGuidsToSkip(),
        batchSize: CHECKBOX_SECTION_PAGINATION_BATCHSIZE,
    });
    const [orgListInput, setOrgListInput] = useState({
        ...orgListParams,
    });
    const errorCB = () => {
        setErrorMessage(null);
    };
    const { data: orgsData, error: orgsError, loading: orgsLoading, getOrgsList, } = useGetOrgsListLazyQuery({ ...orgListInput });
    const { data: userOrgsData, error: userOrgsError, loading: userOrgsLoading, getUserOrgsByUserId, } = useGetUserOrgsByUserIdLazyQuery(userId);
    const { data: userAndGroupsData, error: userAndGroupsError, loading: userAndGroupsLoading, } = useGetUserAndGroupsQuery(groupListInput.searchText, groupListInput.guidsToSkip, groupListInput.batchSize);
    const { data: userByIdData, error: userByIdError, loading: userByIdLoading, getUserById, } = useGetUserDetailsByIdWithUsersListLazyQuery([userId], userId, currentOrgId === -1 ? '-1' : '');
    const { data: createUserData, error: createUserError, loading: createUserLoading, createUser, } = useCreateUserMutation();
    const { data: welcomeEmailData, error: welcomeEmailError, loading: welcomeEmailLoading, sendWelcomeEmail: sendWelcomeEmailFn, } = useSendWelcomeEmailMutation();
    const { data: updateUserData, error: updateUserError, loading: updateUserLoading, updateUser, } = useUpdateUserMutation();
    useEffect(() => {
        if (isOrgUiEnabled() && currentOrgId === -1) {
            getOrgsList();
        }
    }, []);
    useEffect(() => {
        if (isEditMode) {
            if (isOrgUiEnabled() && currentOrgId === -1) {
                getUserOrgsByUserId();
            }
            getUserById();
        }
    }, [isEditMode]);
    useEffect(() => {
        const isOrgEnabled = isOrgUiEnabled();
        if (isEditMode &&
            ((orgsData && userOrgsData) ||
                !(isOrgEnabled && currentOrgId === -1)) &&
            userAndGroupsData &&
            userByIdData &&
            !userAndGroupsLoading &&
            !userByIdLoading) {
            if (isOrgEnabled && currentOrgId === -1) {
                let orgData = orgsData?.Data.orgs || [];
                let preSelectedOrgs = userOrgsData?.Data?.orgs || [];
                if (!selectedOrgsRef.current) {
                    selectedOrgsRef.current = preSelectedOrgs;
                }
                else {
                    preSelectedOrgs = selectedOrgsRef.current;
                }
                const preSelectedOrgIds = preSelectedOrgs.map(record => record.orgId);
                const sortedPreSelectedOrgs = preSelectedOrgs.sort((a, b) => a.orgName.localeCompare(b.orgName));
                if (!orgListInput.pattern) {
                    orgData = [
                        ...sortedPreSelectedOrgs,
                        ...orgData.filter((org) => !preSelectedOrgIds.includes(org.orgId)),
                    ];
                }
                setOrgList(orgData);
                setFilteredOrgList(orgData);
                setOrgListIds(orgData.map((record) => ({ id: record.orgId })));
                setPreSelectedOrgListIds(preSelectedOrgIds);
            }
            let userGroupData = userAndGroupsData?.getUserGroupsPrincipalList?.objects || [];
            let preSelected = userByIdData?.listUserGroupById.filter(item => item.id !== getAllGroupGUID()) || [];
            const adminGroupId = getAdminGroupGuid();
            const editedUser = userByIdData?.getUserDetailById?.[0];
            const isCurrentUser = editedUser?.id === getUserGuid();
            if (adminGroupId &&
                hasImplicitAdminAccess(editedUser?.isSuperUser, isCurrentUser) &&
                !preSelected.some((g) => g.id === adminGroupId)) {
                preSelected = [
                    ...preSelected,
                    {
                        id: adminGroupId,
                        name: ADMINISTRATOR_GROUP_NAME,
                    },
                ];
                if (!userGroupData.some((g) => g.id === adminGroupId)) {
                    userGroupData = [
                        ...userGroupData,
                        {
                            id: adminGroupId,
                            name: ADMINISTRATOR_GROUP_NAME,
                        },
                    ];
                }
            }
            if (!selectedGroupsRef.current) {
                selectedGroupsRef.current = preSelected;
            }
            else {
                preSelected = selectedGroupsRef.current;
            }
            const preSelectedIds = preSelected.map(item => item.id);
            const sortedPreSelected = preSelected.sort((a, b) => a.name.localeCompare(b.name));
            if (!groupListInput.searchText) {
                userGroupData = [
                    ...sortedPreSelected,
                    ...userGroupData.filter((group) => !preSelectedIds.includes(group.id)),
                ];
            }
            setUserGroupList(userGroupData);
            setFilteredUserGroupList(userGroupData);
            setGroupListIds(userGroupData.map((record) => ({ id: record.id })));
            setPreSelectedGroupListIds(preSelectedIds);
            setIsEditReady(true);
            setIsLoading(false);
        }
    }, [
        isEditMode,
        orgsData,
        orgsLoading,
        userOrgsData,
        userOrgsLoading,
        userAndGroupsData,
        userAndGroupsLoading,
        userByIdData,
        userByIdLoading,
    ]);
    useEffect(() => {
        const isOrgEnabled = isOrgUiEnabled();
        if (!isEditMode &&
            userAndGroupsData &&
            (orgsData || !(isOrgEnabled && currentOrgId === -1))) {
            if (isOrgEnabled && currentOrgId === -1) {
                let orgData = orgsData?.Data.orgs || [];
                let preSelectedOrgs = [];
                if (!selectedOrgsRef.current) {
                    selectedOrgsRef.current = preSelectedOrgs;
                }
                else {
                    preSelectedOrgs = selectedOrgsRef.current;
                }
                const preSelectedOrgIds = preSelectedOrgs.map((record) => record.orgId);
                const sortedPreSelectedOrgs = preSelectedOrgs.sort((a, b) => a.orgName.localeCompare(b.orgName));
                if (!orgListInput.pattern) {
                    orgData = [
                        ...sortedPreSelectedOrgs,
                        ...orgData.filter(org => !preSelectedOrgIds.includes(org.orgId)),
                    ];
                }
                setOrgList(orgData);
                setFilteredOrgList(orgData);
                setOrgListIds(orgData.map(record => ({ id: record.orgId })));
            }
            let userGroupData = userAndGroupsData?.getUserGroupsPrincipalList?.objects || [];
            let preSelected = [];
            if (!selectedGroupsRef.current) {
                selectedGroupsRef.current = preSelected;
            }
            else {
                preSelected = selectedGroupsRef.current;
            }
            const preSelectedIds = preSelected.map((item) => item.id);
            const sortedPreSelected = preSelected.sort((a, b) => a.name.localeCompare(b.name));
            if (!groupListInput.searchText) {
                userGroupData = [
                    ...sortedPreSelected,
                    ...userGroupData.filter(group => !preSelectedIds.includes(group.id)),
                ];
            }
            setUserGroupList(userGroupData);
            setFilteredUserGroupList(userGroupData);
            setGroupListIds(userGroupData.map(record => ({ id: record.id })));
            setIsLoading(false);
        }
    }, [userAndGroupsData, userAndGroupsLoading, orgsData, orgsLoading]);
    useEffect(() => {
        if (userByIdError) {
            setIsLoading(false);
        }
    }, [userByIdError, userByIdLoading]);
    useEffect(() => {
        if (userAndGroupsError) {
            setIsLoading(false);
        }
    }, [userAndGroupsError, userAndGroupsLoading]);
    useEffect(() => {
        if (orgsError) {
            setIsLoading(false);
        }
    }, [orgsError, orgsLoading]);
    useEffect(() => {
        if (userOrgsError) {
            setIsLoading(false);
        }
    }, [userOrgsError, userOrgsLoading]);
    useEffect(() => {
        if (createUserData) {
            successBannerMode(MODE.ADD);
            const createUserAction = new UserAction(userActionTypeEnum.CREATE_USER);
            const tUserData = _.cloneDeep(transientUser.current);
            const properties = {
                timestamp: Date.now(),
                userId: createUserData.id,
                visibility: tUserData.visibility,
                shareability: tUserData.visibility === SHARING_VISIBILITY.DEFAULT,
                email: !!tUserData?.properties?.mail,
                welcomeEmail: isWelcomeEmailEnabled() && sendWelcomeEmail,
                optionalMessage: !!tUserData.emailMessage,
                numberOfGroups: tUserData.groups.length,
                isIamV2: isOktaEnabled(),
                state: createUserData?.state || '',
                type: createUserData?.type || '',
            };
            if (tUserData?.orgids) {
                properties.orgids = tUserData.orgids;
            }
            collectEvent(createUserAction, properties);
            sendOnboardingEvent(PqlCaptureEventId.CREATE_USER);
            transientUser.current = null;
            closeModal();
        }
    }, [createUserData, createUserLoading]);
    useEffect(() => {
        if (updateUserData) {
            successBannerMode(MODE.UPDATE);
            const updateUserAction = new UserAction(userActionTypeEnum.UPDATE_USER);
            const tUserData = _.cloneDeep(transientUser.current);
            const { content, userid } = tUserData;
            const properties = {
                userId: userid,
                timestamp: Date.now(),
                visibility: content.visibility,
                shareability: content.visibility === SHARING_VISIBILITY.DEFAULT,
                numberOfGroups: content.assignedGroups.length - 1,
                state: content.state,
                isIamV2: isOktaEnabled(),
            };
            if (content.header?.orgIds) {
                properties.orgids = content.header.orgIds;
            }
            collectEvent(updateUserAction, properties);
            transientUser.current = null;
            closeModal();
        }
    }, [updateUserData, updateUserLoading]);
    const filterListData = (tabs, data = []) => {
        switch (tabs) {
            case 'selectGroups':
                setFilteredUserGroupList(data);
                break;
            case 'selectOrgs':
                setFilteredOrgList(data);
                break;
            default:
                break;
        }
    };
    const onSendWelcomeEmail = async (id, message) => {
        await sendWelcomeEmailFn(id, message, isEditMode);
    };
    const onSuccess = async (newData) => {
        setIsLoading(true);
        const { emailMessage, ...newFormData } = newData;
        const formData = {
            ...newFormData,
            isAllOrgContext: currentOrgId === -1,
        };
        transientUser.current = { ...formData, emailMessage };
        try {
            if (isEditMode) {
                await updateUser(formData);
            }
            else {
                const createUserData = await createUser(formData);
                id.current = createUserData.data?.Session__createUser?.id;
                if (isWelcomeEmailEnabled() &&
                    (sendWelcomeEmail ||
                        ((getSubscriptionType() ===
                            SubscriptionType.FREE_TRIAL ||
                            getSubscriptionType() ===
                                SubscriptionType.TEAMS_EDITION) &&
                            optimizeAddUserFlowEnabled()))) {
                    onSendWelcomeEmail(id.current, emailMessage);
                }
            }
            setIsLoading(false);
        }
        catch (err) {
            transientUser.current = null;
            const graphQlError = getErrorObjectFromGraphQLErrors(err);
            const errorCode = getErrorCode(graphQlError);
            const errorText = t(getTranslatedInfoForMessageCode(errorCode).summary);
            setErrorMessage(errorText);
            setIsLoading(false);
            const userAction = new UserAction(isEditMode
                ? userActionTypeEnum.UPDATE_USER
                : userActionTypeEnum.CREATE_USER);
            captureFailureEvent(userAction.name, graphQlError, errorText);
        }
    };
    const updateUserGroupsListInput = useCallback((searchText) => {
        isFilterTouched.current = true;
        setGroupListInput({
            ...groupListInput,
            searchText,
        });
    }, [groupListInput]);
    const updateOrgsListInput = useCallback((searchText) => {
        isFilterTouched.current = true;
        setOrgListInput({
            ...orgListInput,
            pattern: searchText,
        });
    }, [orgListInput]);
    return (React.createElement(React.Fragment, null, (!isEditMode || (isEditMode && isEditReady)) && (React.createElement(CreateEditForm, { userData: userByIdData, message: errorMessage, isEditMode: isEditMode, isModalOpened: isModalOpened, setShowError: errorCB, closeModal: closeModal, onSuccess: onSuccess, groupListIds: groupListIds, orgListIds: orgListIds, preSelectedGroupListIds: preSelectedGroupListIds, preSelectedOrgListIds: preSelectedOrgListIds, userGroupList: userGroupList, orgList: orgList, filteredUserGroupList: filteredUserGroupList, filteredOrgList: filteredOrgList, filterListData: filterListData, onSendWelcomeEmail: onSendWelcomeEmail, sendWelcomeEmail: sendWelcomeEmail, setSendWelcomeEmail: setSendWelcomeEmail, setUserGroupsListInput: updateUserGroupsListInput, setOrgsListInput: updateOrgsListInput, areUserAndGroupsLoading: userAndGroupsLoading, isOrgListLoading: orgsLoading, currentOrgId: currentOrgId, selectedGroupsRef: selectedGroupsRef, selectedOrgsRef: selectedOrgsRef }))));
};
//# sourceMappingURL=create-edit-modal.container.js.map